<template>
    <div id="app-view"
         :style="{ 'background-color': $store.state.UI.backgroundColor }"
    >
        <splitpanes ref="splitPanes" :dbl-click-splitter="false" class="default-theme" @resize="paneResized"
                    @splitter-click="splitterClicked($event)"
        >
            <pane id="main-layout-left-pane" ref="left" :min-size="defaultSidebarSize" :size="paneLeft">
                <MainLayoutLeft />
            </pane>
            <pane id="main-layout-middle-pane" ref="middle" :size="paneMiddle">
                <MainLayoutMiddle />
            </pane>
            <pane id="main-layout-right-pane" ref="right" :min-size="defaultSidebarSize" :size="paneRight">
                <MainLayoutRight />
            </pane>
        </splitpanes>

        <Templates />
        <Modals />
    </div>
</template>

<script>
import "@/services/indexedDB.service.js";

import MainLayoutLeft from '@/components/app/ui/layout/Left';
import MainLayoutMiddle from '@/components/app/ui/layout/Middle';
import MainLayoutRight from '@/components/app/ui/layout/Right';

import LoadScripts from "@/mixins/LoadScripts";

import Templates from "@/components/app/ui/Templates";
import Modals from "@/components/app/ui/Modals";

import { detectSafari } from "@/assets/js/Utilities";
import Cookies from 'js-cookie';

import { Pane, Splitpanes } from 'splitpanes'
import 'splitpanes/dist/splitpanes.css'

export default {
    mixins: [LoadScripts],
    components: {
        MainLayoutLeft,
        MainLayoutMiddle,
        MainLayoutRight,

        Templates,
        Modals,

        Splitpanes,
        Pane,
    },

    data() {
        return {
            paneLeft: null,
            paneRight: null,
            paneMiddle: null,
            lastClick: Date.now()
        }
    },

    computed: {
        defaultSidebarSize() {
            if (window.innerWidth > 1920) return 15;
            else return 18
        },
    },

    created() {
        this.paneLeft = this.defaultSidebarSize;
        this.paneRight = this.defaultSidebarSize;
        this.paneMiddle = 100 - this.paneLeft - this.paneRight;
    },

    async mounted() {
        import("@/assets/js/Boot");

        await new Promise(resolve => {
            const interval = setInterval(() => {
                if (window.UI && window.gpu) {
                    clearInterval(interval);
                    UI.startApp();
                    resolve();
                }
            }, 100)
        })

        await this.loadScripts();
        this.$store.dispatch("App/setScriptsLoaded");

        this.detectMobile();

        this.$nextTick(() => {
            setTimeout(() => {
                if (!gpu.renderer.capabilities.isWebGL2) {
                    this.$snackbar('Your browser doesn\'t seem to support WebGL2 which is required for LUTs Previewer. Please upgrade to a modern browser.', 'error', {
                        class: 'browser-notice',
                        timeout: -1
                    })

                    return
                }
            }, 1000)
        })

        if (detectSafari()) {
            if (Cookies.get('SafariPrivateModeInfoRead')) return;

            this.$snackbar(`Please note, Safari browser does not support some of the LUTs Previewer internal methods when in Private Mode leading to errors and instability. We strongly recommend to use fylm.ai in Safari in Standard browsing mode.`, "error", {
                class: 'browser-notice',
                timeout: -1,
                clickHandler() {
                    Cookies.set('SafariPrivateModeInfoRead', true);
                }
            });
        }
    },

    methods: {
        paneResized() {
            window.UI.paneResized();
        },

        splitterClicked($event) {
            let timeClicked = Date.now();
            if (timeClicked - this.lastClick > 250) {
                this.lastClick = timeClicked;
                return;
            }

            if ($event.index == 1) {
                let rightPaneSize = this.$refs.right._data.style.width.split('%')[0];
                this.$refs.splitPanes.panes[0].size = this.defaultSidebarSize;
                this.$refs.splitPanes.panes[1].size = 100 - this.defaultSidebarSize - parseFloat(rightPaneSize);
            }

            if ($event.index == 2) {
                let leftPaneSize = this.$refs.left._data.style.width.split('%')[0];
                this.$refs.splitPanes.panes[2].size = this.defaultSidebarSize;
                this.$refs.splitPanes.panes[1].size = 100 - this.defaultSidebarSize - parseFloat(leftPaneSize);
            }

            setTimeout(() => {
                window.UI.paneResized();
            }, 200)
        },

        detectMobile() {
            if (
                navigator.userAgent.match(/Android/i) ||
                navigator.userAgent.match(/webOS/i) ||
                navigator.userAgent.match(/iPhone/i) ||
                navigator.userAgent.match(/iPad/i) ||
                navigator.userAgent.match(/iPod/i) ||
                navigator.userAgent.match(/BlackBerry/i) ||
                navigator.userAgent.match(/Windows Phone/i)
            ) {
                this.$snackbar(`At this stage LUTs Previewer works on desktop only`, "error", {
                    timeout: -1,
                });
            }
        }
    }

};
</script>

<style lang="scss">
@import "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.12.1/css/all.min.css";
@import "~zurb-twentytwenty/css/twentytwenty-no-compass.css";
@import "~zurb-twentytwenty/css/twentytwenty.css";
@import "~jquery-ui-bundle/jquery-ui.min.css";
@import "~round-slider/dist/roundslider.min.css";
@import "@/assets/scss/main.scss";

#main-layout-left-pane,
#main-layout-right-pane {
    background-color: var(--color-bg-dark);;
    overflow: visible;
}

#app-view {
    color: #989898 !important;
    background-color: var(--color-bg-gray);
    height: 100vh;
    width: 100vw;
    position: relative;
    z-index: 10;

    .splitpanes__splitter {
        border-left: initial;
        background: transparent;
        //width: 20px;
        z-index: 30;
    }

    #main-layout-left-pane + .splitpanes__splitter {
        margin-left: -1px;
    }

    #main-layout-middle-pane + .splitpanes__splitter {
        margin-right: -3px;
        margin-left: initial;
        &:before {
            right: 0;
            left: initial;
            transform: translate(-50%, -50%);
        }
    }

    .splitpanes__splitter {
        &:before {
            height: 15px;
            width: 4px;
            border-left: 1px solid rgb(135, 135, 135);
            border-right: 1px solid rgb(135, 135, 135);
        }

        &:after {
            display: none;
        }
    }

    //display: grid;
    //grid-template-columns: $sidebar-container-width calc(100vw - #{$sidebar-container-width} - #{$thumbnails-container-width}) $thumbnails-container-width;
    //grid-template-areas:
    //	"main-layout-left main-layout-middle main-layout-right";

    //grid-template-areas:
    //	"sidebar status previews"w
    //	"sidebar main previews"
    //	"sidebar IDT-ODT previews"
    //	"add-block tools previews";

    &.init {
        .splitpanes__splitter {
            display: none;
        }

        //grid-template-rows: $status-container-height 1fr $IDTODT-container-height $tools-container-height;
        //grid-template-areas:
        //	"sidebar status previews"
        //	"sidebar main previews"
        //	"sidebar IDT-ODT previews"
        //	"add-block tools previews";
    }

    //&::before {
    //	content: "";
    //	width: 300px;
    //	height: 100vh;
    //	background: $dark-gray-bg;
    //	position: absolute;
    //	top: 0;
    //	left: 0;
    //	z-index: -1;
    //}

    .template {
        display: none;
    }

    .disabled {
        cursor: not-allowed !important;
        opacity: 0.3;
    }
}

.file-options-container {
	z-index: 10 !important;
	border-radius: var(--box-rounding);

	&.--level2 {
		min-width: initial !important;

		&.--rating {
			.v-list-item {
				display: flex !important;
				align-items: center;
			}

			.v-list-item__title {
				margin-left: 0 !important;
			}
		}
	}

	&.--overflow {
		padding: 25px 0;
		background: var(--dark-gray);

		.v-list {
			height: 340px;
			padding: 0;
			overflow-y: scroll;
			position: relative;
			display: flex;
			flex-direction: column;

			scrollbar-color: #989898 transparent;
			scrollbar-width: thin;

			&::-webkit-scrollbar {
				width: 5px;
			}

			&::-webkit-scrollbar-thumb {
				background-color: #989898;
				border-radius: 50px;
			}
		}
	}

	.v-list {
		background: var(--dark-gray);
		padding: 25px 0;

		.v-list-item {
			min-height: 20px;
			padding: 0 25px;
			display: grid;
			grid-template-columns: 18px 1fr auto auto;
			cursor: pointer;
			position: relative;

			~ .v-list-item {
				margin-top: 2px;
			}

			i:last-of-type {
				//margin-left: 1rem;
			}

			&.--first {
				order: -1;
			}

			> * {
				z-index: 2;
			}

			&:before {
				display: none;
			}

			&:hover:after {
				content: '';
				position: absolute;
				display: flex;
				width: calc(100% - 20px);
				height: calc(100%);
				top: 50%;
				left: 50%;
				transform: translate(-50%, -50%);
				background: rgb(54, 54, 54);
				border-radius: 5px;
				z-index: 1;
			}

			&.full-span {
				grid-template-columns: 1fr;
			}

			&.stars-icons-container {
				grid-template-columns: repeat(5, 1fr);
			}

			&[aria-expanded="true"] {
				//background: rgb(54, 54, 54);
			}

			&.--selected {
				.material-icons,
				.v-list-item__title {
					color: var(--primary);
				}
			}

			&.v-list-item--disabled {
				opacity: 0.6;
			}

			.material-icons, .material-symbols-outlined {
				color: #989898;
				font-size: 18px;
				width: 18px;
			}

			.v-list-item__title {
				margin-left: 4px;
				margin-right: 4px;
				color: #989898;
				font-size: 1rem;
				line-height: 2;
				display: flex;
				align-items: center;
			}

			&:after {
				display: none;
			}
		}
	}

	.control-label--new {
		color: white;
		background: var(--color-accent);
		border-radius: 5px;
		padding: 3px 8px;
		font-size: 0.8rem;
		margin-left: auto;
	}
}

</style>
